import React, { useEffect, useRef, useState } from 'react';

/**
 * Accessible info tooltip. A small "i" trigger that reveals a rich popover on
 * hover, focus, or click. Uses fixed positioning computed from the trigger rect
 * so it is never clipped by the scrolling matrix container.
 */
export function Info({
  title,
  children,
  width = 340,
  label,
}: {
  title?: string;
  children: React.ReactNode;
  width?: number;
  /** Optional visible text before the icon (e.g. a column label that is itself the trigger). */
  label?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const show = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
    setPos({ top: r.bottom + 8, left });
    setOpen(true);
  };
  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', hide, true);
    };
  }, [open]);

  return (
    <span className="info-wrap" onMouseEnter={show} onMouseLeave={hide}>
      {label ? <span className="info-label">{label}</span> : null}
      <button
        ref={btnRef}
        type="button"
        className="info-btn"
        aria-label={title ? `About ${title}` : 'More information'}
        onFocus={show}
        onBlur={hide}
        onClick={() => (open ? hide() : show())}
      >
        i
      </button>
      {open && pos ? (
        <span className="info-pop" role="tooltip" style={{ top: pos.top, left: pos.left, width }}>
          {title ? <span className="info-title">{title}</span> : null}
          <span className="info-body">{children}</span>
        </span>
      ) : null}
    </span>
  );
}
